import React, { useEffect, useState } from 'react';
import { Box, CardContent, Grid, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import {
    IconArrowDownRight,
    IconArrowUpRight,
    IconAlertTriangle,
    IconWallet,
    IconTrendingUp,
    IconTrendingDown,
    IconScale,
    IconArrowRight
} from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import AnimatedStatCard from '../../../../../ui-component/extended/AnimatedStatCard';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import GradientIconBadge from '../../../../../ui-component/extended/GradientIconBadge';
import { gradients } from '../../../../../themes/gradients';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const FinancialDashboard = () => {
    const history = useHistory();
    const [appId, setAppId] = useState(null);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/financial/summary/`);
            setSummary(res.data);
        })();
    }, [appId]);

    if (!summary) return null;

    return (
        <Box>
            <PageHeader
                icon={IconWallet}
                gradient="teal"
                title="Dashboard Financeiro"
                subtitle="Contas a pagar, a receber e fluxo de caixa"
            />

            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <AnimatedStatCard
                        index={0}
                        icon={IconArrowUpRight}
                        gradient="coral"
                        label="A pagar (em aberto)"
                        value={formatCurrencyBR(summary.payable_open_amount)}
                        subtitle={`${summary.payable_open_count} conta(s)`}
                        onClick={() => history.push('/dashboard/financial/payable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnimatedStatCard
                        index={1}
                        icon={IconArrowDownRight}
                        gradient="teal"
                        label="A receber (em aberto)"
                        value={formatCurrencyBR(summary.receivable_open_amount)}
                        subtitle={`${summary.receivable_open_count} conta(s)`}
                        onClick={() => history.push('/dashboard/financial/receivable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnimatedStatCard
                        index={2}
                        icon={IconAlertTriangle}
                        gradient="amber"
                        label="Contas a pagar vencidas"
                        value={formatCurrencyBR(summary.payable_overdue_amount)}
                        subtitle={`${summary.payable_overdue_count} conta(s)`}
                        onClick={() => history.push('/dashboard/financial/payable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnimatedStatCard
                        index={3}
                        icon={IconAlertTriangle}
                        gradient="amber"
                        label="Contas a receber vencidas"
                        value={formatCurrencyBR(summary.receivable_overdue_amount)}
                        subtitle={`${summary.receivable_overdue_count} conta(s)`}
                        onClick={() => history.push('/dashboard/financial/receivable')}
                    />
                </Grid>
            </Grid>

            <Typography variant="h5" fontWeight={700} mb={2} sx={{ letterSpacing: '-0.02em' }}>
                Mês corrente
            </Typography>
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard
                        index={4}
                        icon={IconTrendingUp}
                        gradient="teal"
                        label="Entradas no mês"
                        value={formatCurrencyBR(summary.month_entradas)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard
                        index={5}
                        icon={IconTrendingDown}
                        gradient="coral"
                        label="Saídas no mês"
                        value={formatCurrencyBR(summary.month_saidas)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard
                        index={6}
                        icon={IconScale}
                        gradient="blue"
                        label="Saldo do mês"
                        value={formatCurrencyBR(summary.month_saldo)}
                        onClick={() => history.push('/dashboard/financial/cashflow')}
                    />
                </Grid>
            </Grid>

            <Box component={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <GlassCard
                    accent={gradients.blue.main}
                    hoverable
                    onClick={() => history.push('/dashboard/financial/cashflow')}
                    sx={{ cursor: 'pointer' }}
                >
                    <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <GradientIconBadge icon={IconWallet} gradient="blue" size={38} iconSize={18} />
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Ver fluxo de caixa completo
                                </Typography>
                            </Stack>
                            <IconArrowRight size={20} />
                        </Stack>
                    </CardContent>
                </GlassCard>
            </Box>
        </Box>
    );
};

export default FinancialDashboard;
